import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, Loader2, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useLanguage } from '../context/LanguageContext';

// Legacy visual identity (colors + mood) — from the old "Visuell" tab. Pure DATA
// editing: loads r.data.visualIdentity, saves via PUT /settings/brand-dna/visual-identity
// (writes raw_json.brandDNA.visualIdentity — the LEGACY field the image path reads
// for colors_text / mood). Endpoint + write target unchanged. Does NOT touch the
// image pipeline itself.
//
// (`mood` uses an inline tag editor here — behaviourally identical to the old
// Settings TagInput, just not importing that Settings-internal helper.)
const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const BrandLegacyVisualEditor = ({ getAuthHeader }) => {
  const { t } = useLanguage();
  const [visual, setVisual] = useState({ primaryColors: [], visualType: '', mood: [] });
  const [newColorHex, setNewColorHex] = useState('#00D9C8');
  const [newMood, setNewMood] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() });
      const vi = r.data?.visualIdentity || {};
      setVisual({
        primaryColors: vi.primaryColors || [],
        visualType: vi.visualType || '',
        mood: vi.mood || [],
      });
    } catch { }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    const payload = next || visual;
    setSaving(true);
    try {
      await axios.put(`${API}/settings/brand-dna/visual-identity`, payload, { headers: getAuthHeader() });
      toast.success(t?.common?.saved || 'Gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const addColor = () => {
    if (!newColorHex) return;
    const next = { ...visual, primaryColors: [...(visual.primaryColors || []), { hex: newColorHex, name: newColorHex }] };
    setVisual(next); setNewColorHex('#00D9C8'); save(next);
  };
  const removeColor = (idx) => {
    const next = { ...visual, primaryColors: visual.primaryColors.filter((_, i) => i !== idx) };
    setVisual(next); save(next);
  };
  const addMood = () => {
    const v = newMood.trim();
    if (!v) return;
    const next = { ...visual, mood: [...(visual.mood || []), v] };
    setVisual(next); setNewMood(''); save(next);
  };
  const removeMood = (idx) => {
    const next = { ...visual, mood: visual.mood.filter((_, i) => i !== idx) };
    setVisual(next); save(next);
  };

  return (
    <Card className="bg-[#1A2B42] border-transparent">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#00D9C8]" />{t?.settings?.visual?.brandColorsAndMood || 'Farben & Mood'}
        </CardTitle>
        <CardDescription className="text-[#A0AEC0] text-sm">
          {saving ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> …</span> : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Colors */}
        <div>
          <label className="text-[#5A6C7D] text-xs block mb-2">{t?.settings?.visual?.primaryColors || 'Markenfarben'}</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {(visual.primaryColors || []).map((color, idx) => (
              <div key={idx} className="flex items-center gap-1.5 bg-[#0A1628] rounded-lg px-2 py-1">
                <span className="w-4 h-4 rounded" style={{ background: color.hex, border: '1px solid rgba(255,255,255,0.1)' }} />
                <span className="text-xs text-[#A0AEC0] font-mono">{color.hex}</span>
                <button onClick={() => removeColor(idx)} className="text-[#5A6C7D] hover:text-red-400"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="color" value={newColorHex} onChange={(e) => setNewColorHex(e.target.value)} className="w-9 h-9 rounded-lg cursor-pointer border-0 bg-transparent" data-testid="brand-dna-color-picker" />
            <Button onClick={addColor} disabled={saving} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-8 px-3 text-xs">
              {t?.common?.add || 'Hinzufügen'}
            </Button>
          </div>
        </div>

        {/* Mood */}
        <div>
          <label className="text-[#5A6C7D] text-xs block mb-2">Mood</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(visual.mood || []).map((m, idx) => (
              <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-[#00D9C8]/15 text-[#00D9C8] flex items-center gap-1">
                {m}<button onClick={() => removeMood(idx)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input value={newMood} onChange={(e) => setNewMood(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMood(); } }}
              placeholder={t?.settings?.visual?.moodPlaceholder || 'z. B. modern, warm'}
              className="bg-[#0A1628] border-transparent text-white text-sm" data-testid="brand-dna-mood-input" />
            <Button onClick={addMood} disabled={saving} className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] h-8 px-3 text-xs">
              {t?.common?.add || 'Hinzufügen'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BrandLegacyVisualEditor;
